import { supabase } from "@/integrations/supabase/client";
import type { SharedRecord } from "@/integrations/supabase/types";

export interface PublicSharingError {
  error: string;
  message: string;
  code?: 'NOT_FOUND' | 'EXPIRED' | 'INACTIVE' | 'UNKNOWN';
}

/**
 * Получение публичного контента записи по токену
 */
export const getSharedRecordByToken = async (
  token: string
): Promise<SharedRecord | PublicSharingError> => {
  try {
    const { data, error } = await supabase.rpc('get_shared_record', {
      p_token: token
    });

    if (error) {
      // Обработка специфичных ошибок
      if (error.message.includes('not found') || error.message.includes('expired')) {
        return {
          error: 'LINK_NOT_FOUND_OR_EXPIRED',
          message: 'Ссылка не найдена или срок её действия истёк',
          code: 'NOT_FOUND'
        };
      }

      return {
        error: 'FETCH_FAILED',
        message: error.message,
        code: 'UNKNOWN'
      };
    }

    if (!data || data.length === 0) {
      return {
        error: 'NO_DATA',
        message: 'Запись не найдена',
        code: 'NOT_FOUND'
      };
    }

    return data[0];
  } catch (error) {
    console.error('Error fetching shared record:', error);
    return {
      error: 'UNKNOWN_ERROR',
      message: 'Произошла неизвестная ошибка при получении записи',
      code: 'UNKNOWN'
    };
  }
};

/**
 * Запись аналитики просмотра (вызывается при открытии shared ссылки)
 */
export const recordShareView = async (
  sharedLinkId: string,
  viewerInfo?: {
    userAgent?: string;
  }
): Promise<string | PublicSharingError> => {
  try {
    const { data, error } = await supabase.rpc('record_share_view', {
      p_shared_link_id: sharedLinkId,
      p_viewer_ip: null, // IP будет получен на стороне сервера из заголовков
      p_user_agent: viewerInfo?.userAgent || null
    });

    if (error) {
      return {
        error: 'ANALYTICS_FAILED',
        message: error.message,
        code: 'UNKNOWN'
      };
    }

    return data; // Возвращаем ID записи аналитики
  } catch (error) {
    console.error('Error recording share view:', error);
    return {
      error: 'UNKNOWN_ERROR',
      message: 'Произошла ошибка при записи аналитики',
      code: 'UNKNOWN'
    };
  }
};

/**
 * Получение информации о посетителе (для аналитики)
 * IP адрес будет получен на стороне сервера через заголовки запроса
 */
export const getVisitorInfo = async (): Promise<{
  userAgent?: string;
  timestamp?: string;
}> => {
  return {
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString()
  };
};

/**
 * Проверка валидности токена без получения данных записи
 */
export const validateShareToken = async (
  token: string
): Promise<boolean | PublicSharingError> => {
  try {
    const { data, error } = await supabase
      .from('shared_links')
      .select('id, is_active, expires_at')
      .eq('token', token)
      .single();

    if (error) {
      if (error.code === 'PGRST116') { // No rows returned
        return {
          error: 'TOKEN_NOT_FOUND',
          message: 'Токен не найден',
          code: 'NOT_FOUND'
        };
      }

      return {
        error: 'VALIDATION_FAILED',
        message: error.message,
        code: 'UNKNOWN'
      };
    }

    // Проверяем активность
    if (!data.is_active) {
      return {
        error: 'TOKEN_INACTIVE',
        message: 'Ссылка деактивирована',
        code: 'INACTIVE'
      };
    }

    // Проверяем срок действия
    if (data.expires_at && new Date(data.expires_at) < new Date()) {
      return {
        error: 'TOKEN_EXPIRED',
        message: 'Срок действия ссылки истёк',
        code: 'EXPIRED'
      };
    }

    return true;
  } catch (error) {
    console.error('Error validating share token:', error);
    return {
      error: 'UNKNOWN_ERROR',
      message: 'Произошла ошибка при проверке токена',
      code: 'UNKNOWN'
    };
  }
};

/**
 * Получение метаданных shared ссылки (без содержимого записи)
 */
export const getShareLinkMetadata = async (
  token: string
): Promise<{
  id: string;
  recordName: string;
  createdAt: string;
  expiresAt: string | null;
} | PublicSharingError> => {
  try {
    const { data, error } = await supabase
      .from('shared_links')
      .select(`
        id,
        expires_at,
        created_at,
        records:record_id (
          name,
          created_at
        )
      `)
      .eq('token', token)
      .eq('is_active', true)
      .single();

    if (error) {
      return {
        error: 'METADATA_FETCH_FAILED',
        message: error.message,
        code: 'NOT_FOUND'
      };
    }

    // Проверяем срок действия
    if (data.expires_at && new Date(data.expires_at) < new Date()) {
      return {
        error: 'TOKEN_EXPIRED',
        message: 'Срок действия ссылки истёк',
        code: 'EXPIRED'
      };
    }

    return {
      id: data.id,
      recordName: data.records?.name || 'Без названия',
      createdAt: data.created_at,
      expiresAt: data.expires_at
    };
  } catch (error) {
    console.error('Error fetching share link metadata:', error);
    return {
      error: 'UNKNOWN_ERROR',
      message: 'Произошла ошибка при получении метаданных',
      code: 'UNKNOWN'
    };
  }
};

/**
 * Обновление времени сессии в аналитике (вызывается при закрытии страницы)
 */
export const updateSessionDuration = async (
  analyticsId: string,
  sessionDuration: number // в миллисекундах
): Promise<boolean | PublicSharingError> => {
  try {
    // Конвертируем миллисекунды в PostgreSQL interval формат
    const seconds = Math.floor(sessionDuration / 1000);
    const intervalString = `${seconds} seconds`;

    const { error } = await supabase
      .from('share_analytics')
      .update({ session_duration: intervalString })
      .eq('id', analyticsId);

    if (error) {
      return {
        error: 'SESSION_UPDATE_FAILED',
        message: error.message,
        code: 'UNKNOWN'
      };
    }

    return true;
  } catch (error) {
    console.error('Error updating session duration:', error);
    return {
      error: 'UNKNOWN_ERROR',
      message: 'Произошла ошибка при обновлении времени сессии',
      code: 'UNKNOWN'
    };
  }
};

/**
 * Хук для отслеживания времени на странице
 */
export const useSessionTracking = () => {
  let startTime: number | null = null;
  let analyticsId: string | null = null;

  const startSession = (trackingId: string) => {
    startTime = Date.now();
    analyticsId = trackingId;
  };

  const endSession = async () => {
    if (startTime && analyticsId) {
      const sessionDuration = Date.now() - startTime;
      await updateSessionDuration(analyticsId, sessionDuration);
    }
  };

  // Автоматическое завершение сессии при закрытии страницы
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', endSession);
    window.addEventListener('pagehide', endSession);
  }

  return {
    startSession,
    endSession
  };
};